import {useEffect, useRef, useState} from "react";
import {ArrowLeft} from "lucide-react";
import VideoCard from "./VideoCard";
import getWatchedProgress from "../utils/getWatchedProgress";
import SectionNav from "./SectionNav";
import PlaylistRow from "./PlaylistRow";
import {displayLabel, uiText} from "../utils/videoLanguage";

// Browse view: Section -> Playlist -> ordered videos. Replaces the flat
// category grid when the learner isn't actively searching. The "See all"
// playlist detail lives in the URL (`?playlist=<id>`), so it is a shareable
// permalink, survives refresh, and the browser Back button exits it. The
// player is URL-driven too (`?v=`) and rendered by VideoDashboard.
//
// This component reads the URL but never writes it: VideoDashboard owns the
// whole query string (see the note there on the two-writer clobbering), so
// "See all" and its Back button report up via onOpenPlaylist/onClosePlaylist
// exactly like the cards report the tapped video up via onOpenVideo.
const CatalogBrowser = ({
  catalog,
  language,
  watchHistory,
  updateDailyStat,
  openPlaylistId,
  onOpenPlaylist,
  onClosePlaylist,
  onSeeAllActiveChange,
  onOpenVideo,
}) => {
  const {sections = []} = catalog || {};
  // "See all" playlist-detail view: which playlist is open, from the URL.
  const seeAllId = openPlaylistId || null;
  const [selectedId, setSelectedId] = useState(sections[0]?.id);

  // Keep the selected section valid as the catalog loads/changes.
  useEffect(() => {
    if (sections.length && !sections.some((s) => s.id === selectedId)) {
      setSelectedId(sections[0].id);
    }
  }, [sections, selectedId]);

  // Resolve the open playlist across ALL sections (a ?playlist= deep-link
  // can point anywhere), and keep the section tabs in step with it so
  // closing the detail lands on the owning section's browse rows.
  const ownerSection =
    (seeAllId &&
      sections.find((s) => s.playlists.some((p) => p.id === seeAllId))) ||
    null;
  const seeAllPlaylist =
    ownerSection?.playlists.find((p) => p.id === seeAllId) || null;
  useEffect(() => {
    if (ownerSection && ownerSection.id !== selectedId) {
      setSelectedId(ownerSection.id);
    }
  }, [ownerSection, selectedId]);

  // A seeAllId that doesn't resolve (bad/stale link, or a playlist that fell
  // under the size threshold) leaves seeAllPlaylist null, so this renders as
  // the plain browse rows; the dashboard drops the dead param from the URL.
  const section = sections.find((s) => s.id === selectedId) || sections[0];

  // Let the dashboard hide its header (tagline + progress card) while the
  // detail view is open, so "Back" sits at the top. The cleanup resets it
  // if we unmount mid-detail (e.g. the learner starts a search).
  useEffect(() => {
    if (!onSeeAllActiveChange) return;
    onSeeAllActiveChange(Boolean(seeAllPlaylist));
    return () => onSeeAllActiveChange(false);
  }, [seeAllPlaylist, onSeeAllActiveChange]);

  // Snap to the top of the browser when entering/leaving playlist detail —
  // not on the initial mount (or React Strict Mode's second effect pass),
  // which would scroll past the stats header and clip the tagline.
  const topRef = useRef(null);
  const prevSeeAllIdRef = useRef(seeAllId);
  useEffect(() => {
    if (prevSeeAllIdRef.current === seeAllId) return;
    prevSeeAllIdRef.current = seeAllId;
    topRef.current?.scrollIntoView({block: "start"});
  }, [seeAllId]);

  const renderCard = (v) => (
    <VideoCard
      language={language}
      key={v.id}
      video={v}
      savedTime={watchHistory[v.id]?.lastPosition || 0}
      watchedTime={getWatchedProgress(watchHistory[v.id])}
      onInteraction={() => updateDailyStat("clicks")}
      onExpand={() => onOpenVideo(v)}
    />
  );

  return (
    <>
      {/* scroll-mt matches the dashboard container's padding so snapping here
          lands flush under the header instead of clipping. */}
      <div ref={topRef} aria-hidden="true" className="scroll-mt-6" />

      {seeAllPlaylist ? (
        /* "See all" detail: every video in the playlist as a grid, with a way
           back to the browse rows. */
        <section className="mb-4">
          <button
            type="button"
            onClick={onClosePlaylist}
            className="flex items-center gap-1 text-sm font-semibold text-[#001489] mb-3 px-1 hover:underline"
          >
            <ArrowLeft size={16} />
            {uiText("Back to", language)}{" "}
            {displayLabel(ownerSection.title, language)}
          </button>
          <h3 className="text-base font-semibold text-[#262626] tracking-tight mb-2 px-1">
            {displayLabel(seeAllPlaylist.title, language)}
            <span className="ml-2 text-xs font-medium text-[#262626]/40">
              {seeAllPlaylist.videos.length}{" "}
              {seeAllPlaylist.videos.length === 1 ? "video" : "videos"}
            </span>
          </h3>
          {/* Single column on phones: full-width cards are easier to tap and
              read for this audience than a cramped 2-up grid. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-2">
            {seeAllPlaylist.videos.map(renderCard)}
          </div>
        </section>
      ) : (
        <>
          {/* Orientation no longer renders here (Marco 08-25): the required
          first step lives on the HOME page, shown only to people who still
          owe it. Deep links (?v=) and search still reach intro videos. */}
          <SectionNav
            sections={sections}
            selectedId={selectedId}
            onSelect={setSelectedId}
            language={language}
          />

          {section?.playlists.map((p) => (
            <PlaylistRow
              key={p.id}
              title={p.title}
              language={language}
              videos={p.videos}
              renderCard={renderCard}
              onSeeAll={() => onOpenPlaylist(p.id)}
            />
          ))}

          {section?.looseVideos.length > 0 && (
            <section className="mb-4">
              <h3 className="text-base font-semibold text-[#262626] tracking-tight mb-2 px-1">
                {uiText("More in", language)}{" "}
                {displayLabel(section.title, language)}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-2">
                {section.looseVideos.map(renderCard)}
              </div>
            </section>
          )}

          {sections.length === 0 && (
            <div
              role="status"
              className="flex flex-col items-center justify-center text-center py-16 px-6 rounded-[24px] border border-dashed border-slate-200 bg-slate-50/60"
            >
              <p className="text-base font-semibold text-slate-700">
                {uiText("No videos yet", language)}
              </p>
              <p className="text-sm text-slate-400 mt-1">
                {uiText(
                  "Check back soon — your playlists will appear here.",
                  language,
                )}
              </p>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CatalogBrowser;
